export type WizardStep = {
  step: string;
  data: unknown;
};

type ArrayToUnion<T> = T extends Array<infer U> ? U : never;
type StepList<Steps extends WizardStep[]> = Steps extends [
  infer Step extends WizardStep,
  ...infer Rest extends WizardStep[],
]
  ? [Step['step'], ...StepList<Rest>]
  : [];

type AccumulateData<AccumulatedData extends {}, Step extends WizardStep> = {
  [K in Step['step']]: Step['data'];
} & AccumulatedData;

type NextAndBackActions<
  Prev extends WizardStep | undefined,
  Curr extends WizardStep,
> = Prev extends WizardStep
  ? {
      [K in `TO_${Curr['step']}`]: {
        action: `TO_${Curr['step']}`;
        data: Prev['data'];
      };
    } & {
      [K in `BACK_TO_${Prev['step']}`]: {
        action: `BACK_TO_${Prev['step']}`;
        data?: Partial<Curr['data']>;
      };
    }
  : {};

type StepActions<Step extends WizardStep> = {
  [K in `SET_DATA_${Step['step']}`]: {
    action: `SET_DATA_${Step['step']}`;
    data: Partial<Step['data']>;
  };
};

type BuildActions<
  Steps extends WizardStep[],
  Actions = {
    RESET: {
      action: 'RESET';
      data: undefined;
    };
  },
  Prev extends WizardStep | undefined = undefined,
> = Steps extends [
  infer First extends WizardStep,
  ...infer Rest extends WizardStep[],
]
  ? BuildActions<
      Rest,
      Actions & NextAndBackActions<Prev, First> & StepActions<First>,
      First
    >
  : Actions;

type PartialStateData<Steps extends WizardStep[]> = Steps extends [
  infer First extends WizardStep,
  ...infer Rest extends WizardStep[],
]
  ? { [K in First['step']]?: Partial<First['data']> } & PartialStateData<Rest>
  : {};

type BuildState<
  Steps extends WizardStep[],
  Remaining extends WizardStep[] = Steps,
  AccumulatedData extends {} = {},
> = Remaining extends [
  infer First extends WizardStep,
  ...infer Rest extends WizardStep[],
]
  ?
      | ({
          step: First['step'];
        } & AccumulatedData &
          PartialStateData<Remaining>)
      | BuildState<Steps, Rest, AccumulateData<AccumulatedData, First>>
  : never;

export type WizardState<Steps extends WizardStep[]> = BuildState<Steps>;
export type WizardActionList<Steps extends WizardStep[]> = BuildActions<Steps>;
export type WizardAction<Steps extends WizardStep[]> =
  WizardActionList<Steps>[keyof WizardActionList<Steps>];
export type WizardReducer<Steps extends WizardStep[]> = (
  state: WizardState<Steps>,
  actions: WizardAction<Steps>,
) => WizardState<Steps>;

const getStep = <Steps extends WizardStep[]>(
  steps: StepList<Steps>,
  index: number,
): string | undefined => {
  if (index < 0 || index >= steps.length) {
    return undefined;
  }
  return steps[index];
};

type SimpleAction = {
  action: string;
  data: unknown;
};

type ActionSimpleDescription = {
  step: string;
  index: number;
  action: string;
  type: 'next' | 'previous' | 'setData' | 'reset';
};

type ActionDescription<Step extends string> = {
  [K in `TO_${Step}`]: {
    step: Step;
    index: number;
    action: `TO_${Step}`;
    type: 'next';
  };
} & {
  [K in `BACK_TO_${Step}`]: {
    step: Step;
    index: number;
    action: `BACK_TO_${Step}`;
    type: 'previous';
  };
} & {
  [K in `SET_DATA_${Step}`]: {
    step: Step;
    index: number;
    action: `SET_DATA_${Step}`;
    type: 'setData';
  };
} & {
  RESET: {
    action: 'RESET';
    type: 'reset';
  };
};

const actionsFromStep = <Step extends string>(
  step: Step,
  index: number,
): ActionDescription<Step> => {
  const nextStep: `TO_${Step}` = `TO_${step}`;
  const previousStep: `BACK_TO_${Step}` = `BACK_TO_${step}`;
  const setStepData: `SET_DATA_${Step}` = `SET_DATA_${step}`;
  return {
    [nextStep]: {
      step,
      index,
      action: nextStep,
      type: 'next',
    },
    [previousStep]: {
      step,
      index,
      action: previousStep,
      type: 'previous',
    },
    [setStepData]: {
      step,
      index,
      action: setStepData,
      type: 'setData',
    },
  } as ActionDescription<Step>;
};

/**
 * A utility function for managing the steps of a sequence of actions.
 * Gives the ability to navigate between steps while keeping any filled data
 *
 * Usage :
 *
 * ```typescript
 * type Step1 = { step: 'Step1'; data: { myStep1Data: string } };
 * type Step2 = { step: 'Step2'; data: { myStep2Data: number } };
 * const reducer = wizardUtils<[Step1, Step2]>(['Step1', 'Step2']);
 * // Resulting generated State
 * type State =
 *   | {
 *       step: 'Step1';
 *       Step1: Partial<{ myStep1Data: string }>;
 *       Step2: Partial<{ myStep2Data: number }>;
 *     }
 *   | {
 *       step: 'Step2';
 *       Step1: { myStep1Data: string };
 *       Step2: Partial<{ myStep2Data: number }>;
 *     };
 * // Available actions
 * type Actions = [
 *   {
 *     action: 'TO_Step2';
 *     data: { myStep1Data: string };
 *   },
 *   {
 *     action: 'BACK_TO_Step1';
 *     data: Partial<{ myStep2Data: number }>;
 *   },
 *   {
 *     action: 'SET_DATA_Step1';
 *     data: Partial<{ myStep1Data: string }>;
 *   },
 *   {
 *     action: 'SET_DATA_Step2';
 *     data: Partial<{ myStep2Data: number }>;
 *   },
 *   {
 *     action: 'RESET';
 *     data: undefined;
 *   },
 * ];
 * ```
 */
export const wizardUtils = <Steps extends [WizardStep, ...WizardStep[]]>(
  steps: StepList<Steps>,
): {
  steps: StepList<Steps>;
  initialState: { step: StepList<Steps>[0] };
  reducer: WizardReducer<Steps>;
  actions: string[];
} => {
  const actions = steps.reduce(
    (acc, step, index) => {
      acc = {
        ...acc,
        ...actionsFromStep(step, index),
      };
      return acc;
    },
    {} as ActionDescription<ArrayToUnion<Steps>['step']>,
  );
  actions.RESET = {
    action: 'RESET',
    type: 'reset',
  };

  const reducer = (
    state: WizardState<Steps>,
    action: WizardAction<Steps>,
  ): WizardState<Steps> => {
    const simpleAction = action as SimpleAction;
    const simpleActions = actions as Record<string, ActionSimpleDescription>;
    const actionInfo = simpleActions[simpleAction.action];
    if (!actionInfo) {
      return state;
    }
    if (actionInfo.type === 'next') {
      const expectedInitialStep = getStep<Steps>(steps, actionInfo.index - 1);
      if (expectedInitialStep !== state.step) {
        // If current state is not the step before, this is an error
        return state;
      }
      return {
        ...state,
        [expectedInitialStep]: simpleAction.data,
        step: actionInfo.step,
      };
    } else if (actionInfo.type === 'previous') {
      const expectedInitialStep = getStep<Steps>(steps, actionInfo.index + 1);
      if (expectedInitialStep !== state.step) {
        // If current state is not the step after, this is an error
        return state;
      }
      return {
        ...state,
        [expectedInitialStep]: simpleAction.data
          ? simpleAction.data
          : state[expectedInitialStep],
        step: actionInfo.step,
      };
      // previous
    } else if (actionInfo.type === 'setData') {
      if (actionInfo.step !== state.step) {
        return state;
      }
      return {
        ...state,
        [actionInfo.step]: simpleAction.data,
      };
    }
    // reset
    return {
      step: steps[0],
    } as WizardState<Steps>;
  };

  return {
    initialState: { step: steps[0] },
    steps,
    reducer,
    actions: Object.keys(actions),
  };
};
